// ─── useTaskFilters ───
// Single source of filter state for all 5 task views.
// Filter values are stored in the URL (TanStack Router search params) so that
// filtered views are shareable via link. URL shape == API wire format so there
// is no transform layer between the two (ADR 0011 requirement).
//
// ⚠️ This hook must be used inside a component rendered by the /app/l/$listRef
//    route because it calls Route.useSearch() from that route context (ADR
//    0012 — the friendly-URL list route; see routes/app/l/$listRef.tsx).

import { useMemo, useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  TableSortFieldEnum,
  SortDirectionEnum,
  TaskColumnEnum,
} from '@a8-planner/shared/schemas/views.schema';
import type { SavedViewSort, TaskColumn } from '@a8-planner/shared';
import { useAuth } from '../auth/AuthProvider';
import { ACTIVE_VIEW_OPTIONS, type ActiveView } from '../../stores/ui.store';

// ─── View mode persistence (GHE #284 item 1) ───
// Prior to #284, `features/views/ViewSwitcher.tsx` persisted the active view
// ONLY to a per-list localStorage key — never in the URL — so a shared link
// always opened on whatever view the RECIPIENT last had for that list, and
// `routes/app/l/$listRef.tsx` needed an elaborate deep-link race workaround
// to cope with the one-tick gap between mount (defaulting to 'list') and the
// localStorage-restore effect landing. Lifting `viewMode` into this hook
// (alongside groupBy/sort, which already live here) fixes both: the resolved
// value is available SYNCHRONOUSLY on the very first render (no effect gap),
// and it's shareable. localStorage is kept as a fallback for links that don't
// carry `viewMode` yet (older bookmarks) and as a write-through mirror so a
// later bare link still opens on the same view.

function viewModeStorageKey(listId: string): string {
  return `a8planner-view-${listId}`;
}

// ─── Columnas (GHE #1099) ───
// Reportado por el dueño: `?cols=title%2Cstatus%2Cassignee%2C…` en la barra de direcciones es
// ruido. Y tenía razón — copié el patrón de `sortField`, pero esto es una PREFERENCIA PERSONAL,
// no algo que se comparta: la elección de columnas no dice nada sobre QUÉ tareas estás viendo.
//
// Así que se guarda por lista, igual que `viewMode`, y la URL deja de escribirse. Se sigue
// LEYENDO si viene puesta, para que un enlace preparado a mano siga funcionando y para que una
// vista guardada pueda aplicarla.
function columnsStorageKey(listId: string): string {
  return `a8planner-cols-${listId}`;
}

function readPersistedColumns(listId: string): TaskColumn[] | null {
  try {
    const raw = localStorage.getItem(columnsStorageKey(listId));
    if (!raw) return null;
    const parsed = raw
      .split(',')
      .map((k) => k.trim())
      .filter((k) => TaskColumnEnum.safeParse(k).success) as TaskColumn[];
    return parsed.length > 0 ? parsed : null;
  } catch {
    // Un navegador con el almacenamiento bloqueado no debe tumbar la lista: sin preferencia
    // guardada, se usan las de por defecto.
    return null;
  }
}

function isActiveView(value: string): value is ActiveView {
  return (ACTIVE_VIEW_OPTIONS as readonly string[]).includes(value);
}

function readPersistedViewMode(listId: string): ActiveView {
  try {
    const raw = localStorage.getItem(viewModeStorageKey(listId));
    if (raw && isActiveView(raw)) return raw;
  } catch {
    // localStorage unavailable in SSR or private mode
  }
  return 'list';
}

function writePersistedViewMode(listId: string, view: ActiveView): void {
  try {
    localStorage.setItem(viewModeStorageKey(listId), view);
  } catch {
    // Ignore write failures
  }
}

// ─── Group-by values ───
// Controls how the List view groups tasks. Stored in the URL so the grouping
// is shareable. Default (when absent) is 'client'.
export const GROUP_BY_OPTIONS = ['client', 'status', 'priority', 'assignee'] as const;
export type GroupBy = (typeof GROUP_BY_OPTIONS)[number];

// Filter key names match the TaskFilterSchema wire format exactly.
// All values are raw strings (CSV for multi-value fields) — coercion
// happens server-side. This keeps the URL human-readable and stable.

/** All filter fields the bar can set. Every field is optional. */
export interface TaskFilters {
  /** Free-text search (title or description). */
  q?: string;
  /** CSV of status UUIDs. */
  statusIds?: string;
  /** CSV of Priority values, e.g. "URGENT,HIGH". */
  priorities?: string;
  /** CSV of assignee user UUIDs. */
  assigneeIds?: string;
  /** "true" → include tasks with no assignee. */
  unassigned?: string;
  /** ISO datetime lower bound for dueDate. */
  dueFrom?: string;
  /** ISO datetime upper bound for dueDate. */
  dueTo?: string;
  /** ISO datetime lower bound for startDate. */
  startFrom?: string;
  /** ISO datetime upper bound for startDate. */
  startTo?: string;
  /** Minimum estimate in minutes (coerced to number server-side). */
  estimateMin?: string;
  /** Maximum estimate in minutes (coerced to number server-side). */
  estimateMax?: string;
  /** CSV of client UUIDs. */
  clientIds?: string;
  /** "true" → include tasks with no client. */
  noClient?: string;
  /**
   * JSON-encoded `CustomFieldFilter[]` (`{ fieldId, value }[]`), matching the
   * shared `TaskFilterSchema.customFields` wire format exactly (query-string
   * safe; server does `JSON.parse` + Zod validation, max 10 conditions).
   */
  customFields?: string;
}

/** Return value of useTaskFilters. */
export interface UseTaskFiltersReturn {
  /** Current filter values (always defined, fields are optional). */
  filters: TaskFilters;
  /**
   * Set a single filter field. Pass `undefined` or empty string to clear it.
   * The value is stored as-is in the URL (no coercion here).
   */
  setFilter: (key: keyof TaskFilters, value: string | undefined) => void;
  /** Toggle the current user's ID in/out of assigneeIds ("Me mode"). */
  toggleMe: () => void;
  /** Clear all filters (navigate to same URL with no search params). */
  clearAll: () => void;
  /** True when any filter is currently set. */
  isActive: boolean;
  /** Number of active filter fields (for the chip badge). */
  activeCount: number;
  /**
   * Stable-identity Record ready to append to URLSearchParams.
   * This is the value to include in TanStack Query keys and API calls.
   * Only entries with non-empty values are included.
   */
  apiParams: Record<string, string>;
  /** Current group-by value (defaults to 'client' when absent from URL). */
  groupBy: GroupBy;
  /** Set the group-by value in the URL (replaces history entry). */
  setGroupBy: (value: GroupBy) => void;
  /**
   * Current Table-view sort spec, if any (URL state — GHE #105 saved-views
   * round-trip). `undefined` when unsorted, or when only one of the two URL
   * keys is present (a malformed pair is treated as "no sort", never a crash).
   */
  sort: SavedViewSort | undefined;
  /** Set (or clear, passing `undefined`) the sort spec in the URL (replaces history entry). */
  setSort: (value: SavedViewSort | undefined) => void;
  /**
   * Columnas elegidas para la vista, EN ORDEN, o `null` = «las de por defecto» (GHE #1099).
   * Nunca la lista vacía: una tabla sin columnas no es una elección, es un fallo.
   */
  columns: TaskColumn[] | null;
  /** Fija las columnas; `undefined` limpia la clave y vuelve al conjunto por defecto. */
  setColumns: (value: readonly TaskColumn[] | undefined) => void;
  /**
   * Current view mode (GHE #284 item 1) — resolved from the URL's `viewMode`
   * when present, else this list's persisted localStorage preference, else
   * `'list'`. Always defined and resolved SYNCHRONOUSLY (no effect delay).
   */
  viewMode: ActiveView;
  /** Set the view mode: writes both the URL (`replace`) and localStorage. */
  setViewMode: (value: ActiveView) => void;
}

// ─── The search schema for the $listId route ───
// Exported so the route file can import and use it in `validateSearch`.
// All keys are optional strings. We keep the schema minimal (plain optional
// strings) so TanStack Router doesn't coerce or reject unknown values —
// coercion is the server's job. An explicit `undefined` clears the key.

/**
 * A boolean URL flag that survives TanStack's JSON-ish search parser in BOTH directions.
 *
 * Reading: `?flag=false` arrives as the BOOLEAN false, while a link written before GHE #445
 * (or hand-edited) carries `?flag="false"` and arrives as the STRING "false". Both are accepted,
 * so old shared links keep working — a bare `z.enum(['true','false'])` rejected the boolean form
 * and, with `.catch(undefined)`, did so SILENTLY, quietly ignoring a shared toggle state.
 *
 * Writing: the output is a real `boolean`, which is what keeps the quotes out of the address bar.
 * TanStack's `stringifySearchWith(JSON.stringify, JSON.parse)` re-encodes a STRING whose content
 * happens to be valid JSON as `"false"` — deliberately, so the string round-trips as a string
 * rather than decoding back to a boolean. A real boolean skips that branch and encodes as `false`.
 */
const ganttBoolParam = z
  .union([z.enum(['true', 'false']), z.boolean()])
  .optional()
  .catch(undefined)
  .transform((v): boolean | undefined =>
    v === undefined ? undefined : v === true || v === 'true',
  );

export const listIdSearchSchema = z.object({
  q: z.string().optional(),
  statusIds: z.string().optional(),
  priorities: z.string().optional(),
  assigneeIds: z.string().optional(),
  unassigned: z.string().optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
  startFrom: z.string().optional(),
  startTo: z.string().optional(),
  estimateMin: z.string().optional(),
  estimateMax: z.string().optional(),
  clientIds: z.string().optional(),
  noClient: z.string().optional(),
  customFields: z.string().optional(),
  // Group-by for the List view. Not a filter — does not flow to the API.
  // Default 'client' is applied in the hook when the key is absent.
  groupBy: z.enum(GROUP_BY_OPTIONS).optional(),
  // Deep-link support (Phase 6d Global Search): a task id to auto-open in
  // the detail panel on mount, e.g. navigating here from a search result.
  // Not a filter — does not flow to the API. Consumed once by ListView then
  // stripped from the URL by ListDetailPage (see routes/app/l/$listRef.tsx).
  // `.catch(undefined)`: friendly URLs are shared/bookmarked, so a malformed or
  // stale value must be silently dropped, never crash validateSearch (ADR 0012).
  openTask: z.string().uuid().optional().catch(undefined),
  // Deep-link companion to `openTask` (GHE #106 item 1): the specific comment to scroll
  // into view + transiently highlight once the task detail panel's Comments section has
  // loaded it. Only ever meaningful alongside `openTask` (a comment search result or a
  // COMMENT_ADDED/COMMENT_MENTION notification always sets both together — see
  // useNavigateToSearchResult.ts and features/notifications/useNotificationNavigate.ts) but
  // kept independently optional/`.catch(undefined)` for the same shareable/malformed-URL
  // robustness as every other param here. Consumed once by CommentSection, then stripped
  // from the URL by ListDetailPage alongside openTask.
  comment: z.string().uuid().optional().catch(undefined),
  // Saved Views (Phase 6e, ADR 0011 D5): id of the currently-APPLIED SavedView.
  // Not a filter — does not flow to the task-list API. Chosen over a Zustand
  // store so the applied view is shareable via link, consistent with every
  // other piece of view state already living in this URL (see
  // apps/web/src/features/saved-views/useAppliedView.ts for the drift/apply logic
  // that reads and writes this param).
  // `.catch(undefined)`: same shareable-URL robustness as openTask above.
  view: z.string().uuid().optional().catch(undefined),
  // Table-view sort echo (GHE #105 — audit finding "sort silently dropped from
  // saved views"). Not a task filter — does not flow to the task-list API
  // (mirrors groupBy/view above). Reuses the exact enums the SavedView.sort
  // wire contract is built on (TableSortFieldEnum/SortDirectionEnum,
  // packages/shared/src/schemas/views.schema.ts) so a stored view's sort is
  // always representable here without a lossy string cast. `.catch(undefined)`:
  // same shareable/bookmarkable-URL robustness as openTask/view above.
  sortField: TableSortFieldEnum.optional().catch(undefined),
  sortDir: SortDirectionEnum.optional().catch(undefined),
  // Columnas de la vista (GHE #1099) — CSV de claves, mismo espíritu que `sortField`: no es un
  // filtro, no viaja a la API de tareas, y existe aquí para que una vista guardada pueda
  // redondearlas y para que un enlace compartido lleve puestas las columnas.
  // `.catch(undefined)`: una URL manipulada no debe romper la página, sólo perder la elección.
  cols: z.string().optional().catch(undefined),
  // View mode (GHE #284 item 1): which of the 5 task views (list/board/gantt/
  // calendar/table) is showing for this list — see the module comment above.
  // Not a filter — does not flow to the task-list API. `.catch(undefined)`:
  // same shareable/bookmarkable-URL robustness as every other param here.
  viewMode: z.enum(ACTIVE_VIEW_OPTIONS).optional().catch(undefined),
  // ─── Gantt view state (GHE #437 item 16) ───
  // Weeks-visible zoom + the three toolbar toggles (backlog rail / disabled-users roster /
  // all-names label density) for this list's Gantt tab. Not filters — none of these flow to the
  // task-list API; they only ever apply when `viewMode === 'gantt'`.
  //
  // ⚠️ All four accept BOTH the natural shape and the quoted string one, and normalise to the
  // natural one. TanStack's parser is JSON-ish in both directions: `?ganttWeeks=8` parses to the
  // number 8, while `?ganttWeeks="8"` (what this app itself wrote before GHE #445) parses to the
  // string "8". Accepting only one shape breaks real links — a bare `z.string()` threw
  // `invalid_type` on exactly the shared link this feature exists for, taking the route to the
  // error boundary, and the enum toggles failed the same way SILENTLY via `.catch(undefined)`.
  //
  // Emitting the natural type is what keeps the quotes out of the address bar: TanStack's
  // `stringifySearchWith(JSON.stringify, JSON.parse)` re-encodes a string whose content parses as
  // JSON back through `JSON.stringify`, so "8" becomes `"8"`. That is deliberate round-tripping,
  // not a bug — which is why the fix is to hold the value as a number, not to fight the encoder.
  //
  // Bounds ("a whole number in [1,16]") stay in features/gantt/useGanttViewParams.ts
  // (`clampVisibleWeeks`): a shared filters/ schema should not own the Gantt's zoom limits. This
  // only guarantees "a finite number, or undefined" — a non-finite value (`Number("abc")`)
  // degrades to `undefined` so the key drops out of the URL entirely rather than being re-encoded
  // as the literal `null` that `JSON.stringify(NaN)` would produce.
  ganttWeeks: z
    .union([z.string(), z.number()])
    .optional()
    .catch(undefined)
    .transform((v): number | undefined => {
      if (v === undefined) return undefined;
      const parsed = Number(v);
      return Number.isFinite(parsed) ? parsed : undefined;
    }),
  ganttShowBacklog: ganttBoolParam,
  ganttShowDisabledUsers: ganttBoolParam,
  ganttShowAllTaskNames: ganttBoolParam,
  ganttShowLoad: ganttBoolParam, // GHE #680
});

export type ListIdSearch = z.infer<typeof listIdSearchSchema>;

/**
 * The mutable search record every `navigate({ search: (prev) => … })` updater on this route
 * works with — here, in features/gantt/useGanttViewParams.ts, features/saved-views/
 * useAppliedView.ts and routes/app/l/$listRef.tsx.
 *
 * It is deliberately WIDER than `ListIdSearch`: updaters spread `prev`, add or `delete` keys by
 * name, and hand the result back, so an exact-shape type would reject the intermediate objects.
 * TanStack's `ParamsReducerFn` only requires that the annotation accept the real search type,
 * which this does.
 *
 * ⚠️ It covers `number` and `boolean`, not just `string` (GHE #445). Every param used to be
 * declared a string purely so this one annotation could stay narrow — which cost a pair of
 * quotes in the address bar on every Gantt share link, since TanStack re-encodes a JSON-parseable
 * string as `"8"`. Widening here once is what lets a param be declared as the type it actually
 * is; add to this union rather than reintroducing a string-shaped param.
 */
export type ListSearchRecord = Record<string, string | number | boolean | undefined>;

// ─── Hook ───

export function useTaskFilters(listId: string): UseTaskFiltersReturn {
  // This search is provided by the $listId route that calls validateSearch.
  // We type-assert through `unknown` to avoid importing the generated route tree
  // here, since this hook lives in features/ and route context is threaded in
  // from the page. The caller (ListDetailPage) is the place that owns the route.
  const search = useSearch({ from: '/app/l/$listRef' }) as ListIdSearch;
  const navigate = useNavigate({ from: '/app/l/$listRef' });
  const { user } = useAuth();

  // ─── setFilter ───
  const setFilter = useCallback(
    (key: keyof TaskFilters, value: string | undefined): void => {
      void navigate({
        search: (prev: ListSearchRecord) => {
          const next = { ...prev };
          if (value === undefined || value === '') {
            delete next[key];
          } else {
            next[key] = value;
          }
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );

  // ─── toggleMe ───
  // Adds/removes the current user's ID from the assigneeIds CSV.
  const toggleMe = useCallback((): void => {
    if (!user) return;
    const userId = user.id;
    const current = search.assigneeIds ?? '';
    const ids = current
      ? current
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const idx = ids.indexOf(userId);
    let next: string | undefined;
    if (idx === -1) {
      next = [...ids, userId].join(',');
    } else {
      const without = ids.filter((id) => id !== userId);
      next = without.length > 0 ? without.join(',') : undefined;
    }
    void navigate({
      search: (prev: ListSearchRecord) => {
        const updated = { ...prev };
        if (next === undefined) {
          delete updated.assigneeIds;
        } else {
          updated.assigneeIds = next;
        }
        return updated;
      },
      replace: true,
    });
  }, [user, search.assigneeIds, navigate]);

  // ─── clearAll ───
  // Clears all filter params but preserves groupBy so the user's grouping
  // choice is not lost when they clear filters.
  const clearAll = useCallback((): void => {
    void navigate({
      search: (prev: ListSearchRecord) => {
        const next: ListSearchRecord = {};
        // Preserve groupBy if it was set explicitly
        if (prev.groupBy !== undefined) {
          next.groupBy = prev.groupBy;
        }
        return next;
      },
      replace: true,
    });
  }, [navigate]);

  // ─── setGroupBy ───
  const setGroupBy = useCallback(
    (value: GroupBy): void => {
      void navigate({
        search: (prev: ListSearchRecord) => ({
          ...prev,
          groupBy: value,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // ─── setSort ───
  // GHE #105: lifts Table-view sort into the URL so Saved Views can round-trip
  // it (useAppliedView reads/writes these same two keys directly via
  // useSearch/useNavigate). Passing `undefined` clears both keys.
  // GHE #1099: la elección de columnas a la URL, por lo mismo que el sort — para que una vista
  // guardada pueda redondearla y para que un enlace la lleve puesta. `undefined` limpia la clave
  // y devuelve la vista al conjunto por defecto.
  const setColumns = useCallback(
    (value: readonly TaskColumn[] | undefined): void => {
      try {
        if (value === undefined || value.length === 0) {
          localStorage.removeItem(columnsStorageKey(listId));
        } else {
          localStorage.setItem(columnsStorageKey(listId), value.join(','));
        }
      } catch {
        // Almacenamiento bloqueado: la elección no sobrevive a la recarga, pero la sesión sigue.
      }
      // ─── Dónde vive la elección, según haya vista aplicada o no ───
      //
      // CON vista aplicada (`view=` en la URL): la elección va a la URL. El estado «vista
      // modificada» tiene que sobrevivir a una recarga y poder compartirse, y la URL ya lleva
      // parámetros ahí, así que `cols` no es ruido añadido. Borrarla —como hacía antes— perdía
      // la modificación al recargar.
      //
      // SIN vista aplicada: sólo localStorage, y la URL se queda limpia. Es lo que pidió el
      // dueño: la elección de columnas es una preferencia personal, no dice nada sobre QUÉ
      // tareas estás viendo, y no merece ensuciar la barra de direcciones.
      const hayVistaAplicada = search.view !== undefined;
      if (hayVistaAplicada || search.cols !== undefined) {
        void navigate({
          search: (prev: ListSearchRecord) => {
            const next = { ...prev };
            if (hayVistaAplicada && value !== undefined && value.length > 0) {
              next.cols = value.join(',');
            } else {
              delete next.cols;
            }
            return next;
          },
          replace: true,
        });
      }
      // Fuerza el recálculo: `columns` depende de `search.cols` y de `listId`, y ninguno cambia
      // al escribir en localStorage.
      setColumnsTick((n: number) => n + 1);
    },
    [navigate, listId, search.cols, search.view],
  );

  const setSort = useCallback(
    (value: SavedViewSort | undefined): void => {
      void navigate({
        search: (prev: ListSearchRecord) => {
          const next = { ...prev };
          if (value === undefined) {
            delete next.sortField;
            delete next.sortDir;
          } else {
            next.sortField = value.field;
            next.sortDir = value.direction;
          }
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );

  // ─── Derived state ───
  const filters: TaskFilters = useMemo(
    () => ({
      q: search.q,
      statusIds: search.statusIds,
      priorities: search.priorities,
      assigneeIds: search.assigneeIds,
      unassigned: search.unassigned,
      dueFrom: search.dueFrom,
      dueTo: search.dueTo,
      startFrom: search.startFrom,
      startTo: search.startTo,
      estimateMin: search.estimateMin,
      estimateMax: search.estimateMax,
      clientIds: search.clientIds,
      noClient: search.noClient,
      customFields: search.customFields,
    }),
    [search],
  );

  // Count only fields that have a meaningful (non-empty) value.
  const activeCount = useMemo((): number => {
    return Object.values(filters).filter((v) => v !== undefined && v !== '').length;
  }, [filters]);

  const isActive = activeCount > 0;

  // apiParams: stable Record used in queryKey and API calls.
  // Keys with undefined or empty string values are omitted so the query key is
  // stable when no filters are set (same object shape across renders).
  const apiParams = useMemo((): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') {
        result[key] = value;
      }
    }
    return result;
  }, [filters]);

  // groupBy: read from URL; default to 'client' when absent.
  const groupBy: GroupBy = search.groupBy ?? 'client';

  // sort: read from URL; a lone/malformed half-pair reads as unsorted rather
  // than throwing (mirrors the `.catch(undefined)` robustness on each field).
  const sort: SavedViewSort | undefined = useMemo(() => {
    if (!search.sortField || !search.sortDir) return undefined;
    return { field: search.sortField, direction: search.sortDir };
  }, [search.sortField, search.sortDir]);

  // columns: CSV → array validado. Se descartan las claves que no estén en el catálogo en vez
  // de rechazar la URL entera: perder una columna desconocida es recuperable, quedarse sin
  // vista no. `null` cuando no hay parámetro = «las de siempre», nunca «ninguna».
  // Contador para recalcular tras escribir en localStorage: `columns` depende de la URL y de
  // `listId`, y escribir la preferencia no cambia ninguno de los dos.
  const [columnsTick, setColumnsTick] = useState(0);
  const columns: TaskColumn[] | null = useMemo(() => {
    // La URL manda cuando viene puesta (enlace preparado a mano, vista guardada aplicada);
    // si no, la preferencia de ESTA lista. Resuelto síncronamente, sin hueco de efecto.
    if (search.cols) {
      const parsed = search.cols
        .split(',')
        .map((k) => k.trim())
        .filter((k) => TaskColumnEnum.safeParse(k).success) as TaskColumn[];
      if (parsed.length > 0) return parsed;
    }
    return readPersistedColumns(listId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `columnsTick` fuerza el recálculo
  }, [search.cols, listId, columnsTick]);

  // ─── viewMode (GHE #284 item 1) ───
  // Resolved SYNCHRONOUSLY every render — the URL wins when present, else this
  // list's persisted localStorage preference, else 'list'. No effect gap: this
  // is what lets routes/app/l/$listRef.tsx read the correct view on the very
  // first render instead of needing its old race workaround.
  const viewMode: ActiveView = search.viewMode ?? readPersistedViewMode(listId);

  const setViewMode = useCallback(
    (value: ActiveView): void => {
      writePersistedViewMode(listId, value);
      void navigate({
        search: (prev: ListSearchRecord) => ({
          ...prev,
          viewMode: value,
        }),
        replace: true,
      });
    },
    [listId, navigate],
  );

  // Reconcile the URL and localStorage whenever the RESOLVED view mode
  // changes (new list, explicit switch, or a bare/legacy link with no
  // `viewMode` param yet): a URL value write-throughs to localStorage so a
  // later bare link for this list still opens on the same view; an absent
  // URL value is filled in (`replace`) so the CURRENT url becomes shareable
  // immediately, matching what's already rendered. Neither branch changes
  // what's rendered THIS render — `viewMode` above already resolved it.
  useEffect(() => {
    if (search.viewMode === undefined) {
      void navigate({
        search: (prev: ListSearchRecord) => ({
          ...prev,
          viewMode,
        }),
        replace: true,
      });
    } else {
      writePersistedViewMode(listId, search.viewMode);
    }
  }, [search.viewMode, listId, navigate, viewMode]);

  return {
    filters,
    setFilter,
    toggleMe,
    clearAll,
    isActive,
    activeCount,
    apiParams,
    groupBy,
    setGroupBy,
    sort,
    setSort,
    columns,
    setColumns,
    viewMode,
    setViewMode,
  };
}
